// @flow
/* eslint-disable jsx-a11y/tabindex-no-positive */

import React from "react";
import { Form } from "react-final-form";
import { Link } from "react-router-dom";
import cx from "classnames";

import Button from "../../components/Button";
import Card from "../../components/Card";
import FieldControl from "../../components/FieldControl";
import { FlashMessageConsumer } from "../../components/FlashMessages";
import type { FlashMessageContextValue } from "../../components/FlashMessages/FlashMessageContext";
import FormRow from "../../components/FormRow";
import PageHeader from "../../components/PageHeader";
import PageLayout from "../../components/PageLayout";
import PasswordInput from "../../components/PasswordInput";
import TextInput from "../../components/Input/TextInput";
import Separator from "../../components/Separator";

import { errors } from "../../utils/errors";
import { isFormSubmitting } from "../../utils/helpers";
import * as validators from "../../utils/validators";

import { EMAIL, PASSWORD } from "./formFields";

import styles from "./Login.scss";
import AccountActionsFlashMessage from "./AccountActionsFlashMessage";

type LoginProps = {
  onSubmit: (
    any,
    $PropertyType<FlashMessageContextValue, "addMessage">,
    showCredentialError: () => void,
    handleLoginSuccess: () => void
  ) => void | Promise<any>,
  resetPasswordPath: string,
  header: string,
  linkToInquiry: string,
  inquiryFormLink: boolean
};

type LoginState = {
  isCredentialErrorShowed: boolean
};

class Login extends React.Component<LoginProps, LoginState> {
  state = {
    isCredentialErrorShowed: false
  };

  showCredentialError = () => this.setState({ isCredentialErrorShowed: true });

  handleLoginSuccess = () => {
    this.setState({ isCredentialErrorShowed: false });
  };

  render() {
    const { combineValidators, required, isEmail } = validators;
    const { isCredentialErrorShowed } = this.state;
    const { onSubmit, resetPasswordPath, header, linkToInquiry, inquiryFormLink } = this.props;

    return (
      <PageLayout>
        <PageHeader bottomMargin header={header} />
        <Card>
          <span className={styles.formHeader}>Melden Sie sich mit Ihren Daten an</span>
          <FlashMessageConsumer>
            {({ addMessage }) => (
              <AccountActionsFlashMessage addMessage={addMessage}>
                <Form
                  onSubmit={values =>
                    onSubmit(values, addMessage, this.showCredentialError, this.handleLoginSuccess)
                  }
                  render={({ form, handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                      <FormRow className={cx(styles.row, styles.loginRow)}>
                        <FieldControl
                          name={EMAIL}
                          caption={() => "e-mail"}
                          validate={combineValidators(
                            required(errors.requiredField()),
                            isEmail(errors.isEmailCorrect())
                          )}
                        >
                          {({ input }) => (
                            <TextInput
                              tabIndex="1"
                              type="email"
                              input={input}
                              placeholder="Ihre E-Mail..."
                            />
                          )}
                        </FieldControl>
                        <FieldControl
                          name={PASSWORD}
                          caption={() => "passwort"}
                          validationMessage={isCredentialErrorShowed ? errors.isUserExist() : null}
                          link={() => (
                            <Link
                              tabIndex="4"
                              to={{ pathname: resetPasswordPath }}
                              className={styles.emailLink}
                            >
                              Passwort vergessen?
                            </Link>
                          )}
                          validate={combineValidators(required(errors.requiredField()))}
                        >
                          {({ input }) => (
                            <PasswordInput
                              tabIndex="2"
                              input={input}
                              placeholder="Ihr Passwort..."
                            />
                          )}
                        </FieldControl>
                      </FormRow>
                      <Button
                        type="submit"
                        tabIndex="5"
                        isDisabled={form.getState().invalid || isFormSubmitting(form)}
                        data-testid="button:logIn"
                      >
                        Einloggen
                      </Button>
                    </form>
                  )}
                />
              </AccountActionsFlashMessage>
            )}
          </FlashMessageConsumer>

          {inquiryFormLink && linkToInquiry && (
            <div>
              <Separator />
              <span className={styles.formHeader}>
                Sie haben sich noch nicht für das Ermoeglicherportal registriert?
                <br />
                Starten Sie jetzt Ihre Anfrage und legen Sie sich einen Account für unser Portal an
              </span>
              <Button href={linkToInquiry} asLink type="button" tabIndex="6">
                Anfrage Starten
              </Button>
            </div>
          )}
        </Card>
      </PageLayout>
    );
  }
}

export default Login;
